/** Single source of truth for project cards, hero carousel, and /project/:slug detail pages. */

export const projects = [
  {
    slug: "fliptrac",
    isBlog: false,
    title: "FlipTrac",
    description:
      "Multi-tenant SaaS for Texas car dealers: inventory, expenses, customer records, and deal paperwork on one Supabase project with row-level security so each dealer only sees their data. Public storefronts at /dealer/:slug expose only listing-safe fields. Selling a vehicle walks staff through buyer capture and generates a prepopulated, sign-ready PDF. Google sign-in. Hardest ongoing work: migrating inconsistent legacy dealer data into a single schema.",
    link: "https://fliptrac.vercel.app",
    techTags: [
      "TypeScript",
      "React",
      "Supabase",
      "PostgreSQL",
      "Vercel",
      "RLS",
      "Google OAuth",
      "PDF tooling",
    ],
    detail: {
      overview: {
        intro:
          "Multi-tenant SaaS for independent Texas dealers—one platform per shop, isolated data, and a public storefront without leaking data across tenants.",
        bullets: [
          "Inventory, expenses, and customer records in one place with Supabase RLS",
          "Path-based public listings at /dealer/:slug—only listing-safe fields are public",
          "Sold flow: capture buyer info → generate a sign-ready PDF from your data",
          "Google sign-in; ongoing work is migrating messy legacy dealer data into one schema",
        ],
      },
      problem: {
        qa: [
          {
            q: "What breaks without a product like this?",
            a: "Dealers juggle spreadsheets, DMS exports, and paper—nothing stays in sync, and the same details get re-keyed for every deal.",
          },
          {
            q: "Who is FlipTrac for?",
            a: "Independent Texas dealers who need a single back office plus an online presence for inventory without building IT from scratch.",
          },
          {
            q: "What does success look like?",
            a: "Staff spend less time on paperwork, buyers see consistent listings online, and each dealership’s sensitive data stays behind auth and RLS.",
          },
        ],
        bullets: [
          "Replaces disconnected tools with one tenant-aware app",
          "Reduces duplicate entry between “back office” and customer-facing listings",
        ],
      },
      sections: [
        {
          id: "languages",
          title: "Languages",
          items: ["TypeScript", "JavaScript", "SQL (PostgreSQL)"],
        },
        {
          id: "technologies",
          title: "Technologies & stack",
          items: [
            "React",
            "Supabase (Auth, Postgres, Row Level Security)",
            "Vercel",
            "PDF generation for deal paperwork",
          ],
        },
        {
          id: "apis",
          title: "APIs & integrations",
          items: [
            "Supabase Auth — Google sign-in",
            "PostgREST / Supabase client for data access under RLS",
            "Public read paths scoped to listing-safe fields for /dealer/:slug storefronts",
          ],
        },
        {
          id: "architecture",
          title: "Architecture & product surface",
          items: [
            "Single Supabase project; tenants modeled in Postgres with RLS so dealers only access their own rows",
            "Path-based public listings vs authenticated back-office",
            "Sold-vehicle flow: buyer capture → generated PDF ready for signature",
            "Ongoing ETL challenge: mapping heterogeneous legacy dealer data into one schema",
          ],
        },
      ],
    },
  },
  {
    slug: "heavyrent",
    isBlog: false,
    title: "HeavyRent",
    description:
      "All-in-one heavy equipment rental platform: integrated Stripe payments, booking and scheduling with availability in mind, distance-based delivery pricing, and equipment management so rental businesses can list assets and take online bookings. Built for operators who need payments, scheduling, and delivery logic in one product.",
    link: "https://www.heavy-rental.com/",
    techTags: [
      "TypeScript",
      "React",
      "Stripe",
      "PostgreSQL",
      "Scheduling",
      "Maps / routing",
      "REST APIs",
    ],
    detail: {
      overview: {
        intro:
          "Equipment-rental software for construction and heavy-equipment operators: bookings, payments, scheduling, and delivery pricing in one product instead of five disconnected tools.",
        bullets: [
          "Stripe-backed payments and checkout aligned with rental rules",
          "Scheduling and availability so equipment isn’t double-booked",
          "Distance- or zone-based delivery pricing on top of catalog and inventory",
          "Operator workflows plus a customer-facing booking path",
        ],
      },
      problem: {
        qa: [
          {
            q: "What’s painful today?",
            a: "Teams stitch together quotes, calendars, payments, and delivery math by hand—errors, double-bookings, and slow quotes cost time and margin.",
          },
          {
            q: "Who is HeavyRent for?",
            a: "Rental businesses that need to list assets, take paid bookings, and price delivery without a custom ERP project.",
          },
          {
            q: "What changes when it works?",
            a: "One flow from list → book → pay → fulfill, with rules for utilization, buffers, and delivery cost riding on the same inventory model.",
          },
        ],
        bullets: [
          "Fewer tools to reconcile at the end of the week",
          "Clear separation between what operators see and what customers book",
        ],
      },
      sections: [
        {
          id: "languages",
          title: "Languages",
          items: ["TypeScript", "JavaScript", "SQL"],
        },
        {
          id: "technologies",
          title: "Technologies & stack",
          items: [
            "Stripe — payments and checkout",
            "Scheduling and booking with availability constraints",
            "Equipment catalog and inventory state",
            "Delivery pricing (distance- or zone-based rules)",
          ],
        },
        {
          id: "apis",
          title: "APIs & integrations",
          items: [
            "Stripe — payment intents, webhooks for payment lifecycle",
            "Geospatial / routing services for delivery quotes (as implemented)",
            "Backend APIs for bookings, equipment, and operator dashboards",
          ],
        },
        {
          id: "architecture",
          title: "Architecture & domain",
          items: [
            "End-to-end rental flow: list assets → book → pay → fulfill",
            "Separation of operator tools vs customer booking experience",
            "Rules for utilization, buffers, and delivery cost on top of core inventory",
          ],
        },
      ],
    },
  },
];

export function getProjectBySlug(slug) {
  if (!slug) return undefined;
  return projects.find(
    (p) => p.slug.toLowerCase() === String(slug).toLowerCase()
  );
}
